import React, { useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Auth Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminWaitingList from "./pages/admin/WaitingList";
import AdminUnregistered from "./pages/admin/Unregistered";
import AdminDidntRenew from "./pages/admin/DidntRenew";
import AdminYearArchive from "./pages/admin/YearArchive";
import AdminStudentDetail from "./pages/admin/StudentDetail";
import AdminStudents from "./pages/admin/Students";
import AllStudents from "./pages/admin/AllStudents";
import ViewAs from "./pages/admin/ViewAs";
import AdminTeachers from "./pages/admin/Teachers";
import AdminParents from "./pages/admin/Parents";
import AdminPrograms from "./pages/admin/Programs";
import AdminClasses from "./pages/admin/Classes";
import AdminFeeBands from "./pages/admin/FeeBands";
import AdminPayments from "./pages/admin/Payments";
import AdminActivities from "./pages/admin/Activities";
import AdminLeaveRequests from "./pages/admin/LeaveRequests";
import AdminSettings from "./pages/admin/Settings";
import AdminUsers from "./pages/admin/Users";
import AdminNotifications from "./pages/admin/Notifications";
import AdminParentDetail from "./pages/admin/ParentDetail";
import AdminUnpaidGuardians from "./pages/admin/UnpaidGuardians";
import AdminFinance from "./pages/admin/Finance";
import AdminCallsList from "./pages/admin/CallsList";
import AdminClassDetail from "./pages/admin/ClassDetail";

// Manager Pages
import ManagerDashboard from "./pages/manager/Dashboard";
import ManagerWaitingList from "./pages/manager/WaitingList";
import ManagerStudents from "./pages/manager/Students";
import ManagerTeachers from "./pages/manager/Teachers";
import ManagerParents from "./pages/manager/Parents";
import ManagerPrograms from "./pages/manager/Programs";
import ManagerClasses from "./pages/manager/Classes";
import ManagerAccounts from "./pages/manager/Accounts";
import ManagerLeaveRequests from "./pages/manager/LeaveRequests";
import ManagerExams from "./pages/manager/Exams";
import ManagerNotifications from "./pages/manager/Notifications";

// Teacher Pages
import TeacherDashboard from "./pages/teacher/Dashboard";
import TeacherStudents from "./pages/teacher/Students";
import TeacherAttendance from "./pages/teacher/Attendance";
import AdminAttendance from "./pages/admin/Attendance";
import TeacherHomework from "./pages/teacher/Homework";
import TeacherExams from "./pages/teacher/Exams";
import TeacherLeave from "./pages/teacher/Leave";
import TeacherHours from "./pages/teacher/Hours";
import TeacherStudentOfMonth from "./pages/teacher/StudentOfMonth";
import TeacherNotifications from "./pages/teacher/Notifications";
import TeacherSalary from "./pages/teacher/Salary";
import AdminSalaries from "./pages/admin/Salaries";
import ManagerSalaries from "./pages/manager/Salaries";

// Parent Pages
import ParentDashboard from "./pages/parent/Dashboard";
import ParentDetails from "./pages/parent/Details";
import ParentChildren from "./pages/parent/Children";
import ParentAttendance from "./pages/parent/Attendance";
import ParentHomework from "./pages/parent/Homework";
import ParentGrades from "./pages/parent/Grades";
import ParentPayments from "./pages/parent/Payments";
import ParentActivities from "./pages/parent/Activities";
import ParentPreviousYears from "./pages/parent/PreviousYears";
import ParentPolicies from "./pages/parent/Policies";
import ParentNotifications from "./pages/parent/Notifications";
import ParentRenewal from "./pages/parent/Renewal";

// Shared Pages
import SchoolCalendar from "./pages/shared/Calendar";

// Public Pages
import PublicActivities from "./pages/public/PublicActivities";
import PublicActivityRegister from "./pages/public/PublicActivityRegister";
import Donate from "./pages/public/Donate";
import DonateSuccess from "./pages/public/DonateSuccess";
import ActivityPrintReport from "./pages/admin/ActivityPrintReport";
import AllStudentsPrint from "./pages/admin/AllStudentsPrint";
import AdminDonations from "./pages/admin/AdminDonations";
import SmsCenter from "./pages/admin/SmsCenter";
import Communications from "./pages/admin/Communications";
import FinancialStatus from "./pages/admin/FinancialStatus";
import AdminPTM from "./pages/admin/PTM";
import ParentPTM from "./pages/parent/PTM";
import TeacherPTM from "./pages/teacher/PTM";
import TeacherSpecialArabic from "./pages/teacher/SpecialArabic";
import AdminBooks from "./pages/admin/Books";
import TeacherBooks from "./pages/teacher/Books";
import BookReceiving from "./pages/admin/BookReceiving";
import AdminHomework from "./pages/admin/Homework";
import Curriculum from "./pages/admin/Curriculum";
import GuardianSearch from "./pages/admin/Search";
import TeacherTimetable from "./pages/teacher/Timetable";
import { MaintenanceGate } from "./components/MaintenanceGate";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Branch Manager is a scoped Manager — treat as manager for route access.
        if (user.role === 'branch_manager' && allowedRoles.includes('manager')) {
            return children;
        }
        // Redirect to appropriate dashboard based on role
        const roleRedirects = {
            super_admin: '/admin',
            manager: '/manager',
            branch_manager: '/manager',
            teacher: '/teacher',
            parent: '/parent'
        };
        return <Navigate to={roleRedirects[user.role] || '/login'} replace />;
    }

    return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
            </div>
        );
    }

    if (user) {
        const roleRedirects = {
            super_admin: '/admin',
            manager: '/manager',
            branch_manager: '/manager',
            teacher: '/teacher',
            parent: '/parent'
        };
        return <Navigate to={roleRedirects[user.role] || '/parent'} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ForgotPassword />} />
            <Route path="/public/activities" element={<PublicActivities />} />
            <Route path="/public/activities/:activityId" element={<PublicActivityRegister />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/donate/success" element={<DonateSuccess />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/waiting-list" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminWaitingList /></ProtectedRoute>} />
            <Route path="/admin/pending-list" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminWaitingList mode="pending" /></ProtectedRoute>} />
            <Route path="/manager/pending-list" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminWaitingList mode="pending" /></ProtectedRoute>} />
            <Route path="/admin/unregistered" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminUnregistered /></ProtectedRoute>} />
            <Route path="/admin/didnt-renew" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminDidntRenew /></ProtectedRoute>} />
            <Route path="/admin/archive/:year" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminYearArchive /></ProtectedRoute>} />
            <Route path="/admin/students/:studentId" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminStudentDetail /></ProtectedRoute>} />
            <Route path="/manager/students/:studentId" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminStudentDetail /></ProtectedRoute>} />
            <Route path="/manager/didnt-renew" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminDidntRenew /></ProtectedRoute>} />
            <Route path="/admin/students" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminStudents /></ProtectedRoute>} />
            <Route path="/admin/all-students" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AllStudents /></ProtectedRoute>} />
            <Route path="/admin/view-as" element={<ProtectedRoute allowedRoles={['super_admin']}><ViewAs /></ProtectedRoute>} />
            <Route path="/manager/all-students" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AllStudents /></ProtectedRoute>} />
            <Route path="/admin/teachers" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminTeachers /></ProtectedRoute>} />
            <Route path="/admin/parents" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminParents /></ProtectedRoute>} />
            <Route path="/admin/parents/:parentId" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminParentDetail /></ProtectedRoute>} />
            <Route path="/admin/programs" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPrograms /></ProtectedRoute>} />
            <Route path="/admin/classes" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminClasses /></ProtectedRoute>} />
            <Route path="/admin/classes/:classId" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminClassDetail /></ProtectedRoute>} />
            <Route path="/admin/curriculum" element={<ProtectedRoute allowedRoles={['super_admin']}><Curriculum /></ProtectedRoute>} />
            <Route path="/admin/search" element={<ProtectedRoute allowedRoles={['super_admin']}><GuardianSearch /></ProtectedRoute>} />
            <Route path="/manager/search" element={<ProtectedRoute allowedRoles={['manager', 'branch_manager', 'super_admin']}><GuardianSearch /></ProtectedRoute>} />
            <Route path="/manager/curriculum" element={<ProtectedRoute allowedRoles={['manager', 'branch_manager', 'super_admin']}><Curriculum /></ProtectedRoute>} />
            <Route path="/admin/fee-bands" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminFeeBands /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/unpaid-guardians" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminUnpaidGuardians /></ProtectedRoute>} />
            <Route path="/admin/finance" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminFinance /></ProtectedRoute>} />
            <Route path="/admin/activities" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminActivities /></ProtectedRoute>} />
            <Route path="/manager/activities" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminActivities /></ProtectedRoute>} />
            <Route path="/admin/leave-requests" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminLeaveRequests /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/notifications" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminNotifications /></ProtectedRoute>} />
            <Route path="/admin/calendar" element={<ProtectedRoute allowedRoles={['super_admin']}><SchoolCalendar /></ProtectedRoute>} />
            <Route path="/admin/calls" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminCallsList /></ProtectedRoute>} />

            <Route path="/admin/activities/:activityId/print" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><ActivityPrintReport /></ProtectedRoute>} />
            <Route path="/manager/activities/:activityId/print" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><ActivityPrintReport /></ProtectedRoute>} />
            <Route path="/admin/all-students/print" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AllStudentsPrint /></ProtectedRoute>} />
            <Route path="/manager/all-students/print" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><AllStudentsPrint /></ProtectedRoute>} />
            <Route path="/admin/donations" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminDonations /></ProtectedRoute>} />
            <Route path="/admin/sms" element={<ProtectedRoute allowedRoles={['super_admin']}><SmsCenter /></ProtectedRoute>} />
            <Route path="/manager/sms" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><SmsCenter /></ProtectedRoute>} />
            <Route path="/admin/communications" element={<ProtectedRoute allowedRoles={['super_admin']}><Communications /></ProtectedRoute>} />
            <Route path="/manager/communications" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><Communications /></ProtectedRoute>} />
            <Route path="/admin/financial-status" element={<ProtectedRoute allowedRoles={['super_admin']}><FinancialStatus /></ProtectedRoute>} />
            <Route path="/admin/ptm" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminPTM /></ProtectedRoute>} />
            <Route path="/manager/ptm" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminPTM /></ProtectedRoute>} />
            <Route path="/admin/books" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminBooks /></ProtectedRoute>} />
            <Route path="/teacher/books" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherBooks /></ProtectedRoute>} />
            <Route path="/admin/book-receiving" element={<ProtectedRoute allowedRoles={['super_admin', 'manager']}><BookReceiving /></ProtectedRoute>} />
            <Route path="/admin/homework" element={<ProtectedRoute allowedRoles={['super_admin', 'manager', 'branch_manager']}><AdminHomework /></ProtectedRoute>} />
            <Route path="/manager/homework" element={<ProtectedRoute allowedRoles={['manager', 'branch_manager', 'super_admin']}><AdminHomework /></ProtectedRoute>} />
            <Route path="/teacher/special-arabic" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherSpecialArabic /></ProtectedRoute>} />
            <Route path="/teacher/ptm" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherPTM /></ProtectedRoute>} />
            <Route path="/parent/ptm" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentPTM /></ProtectedRoute>} />

            {/* Manager Routes */}
            <Route path="/manager" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerDashboard /></ProtectedRoute>} />
            <Route path="/manager/users" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminUsers /></ProtectedRoute>} />
            <Route path="/manager/waiting-list" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerWaitingList /></ProtectedRoute>} />
            <Route path="/manager/unregistered" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminUnregistered /></ProtectedRoute>} />
            <Route path="/manager/students" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerStudents /></ProtectedRoute>} />
            <Route path="/manager/teachers" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerTeachers /></ProtectedRoute>} />
            <Route path="/manager/parents" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerParents /></ProtectedRoute>} />
            <Route path="/manager/parents/:parentId" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminParentDetail /></ProtectedRoute>} />
            <Route path="/manager/programs" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerPrograms /></ProtectedRoute>} />
            <Route path="/manager/classes" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerClasses /></ProtectedRoute>} />
            <Route path="/manager/classes/:classId" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminClassDetail /></ProtectedRoute>} />
            <Route path="/manager/fee-bands" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminFeeBands /></ProtectedRoute>} />
            <Route path="/manager/payments" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminPayments /></ProtectedRoute>} />
            <Route path="/manager/accounts" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerAccounts /></ProtectedRoute>} />
            <Route path="/manager/unpaid-guardians" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminUnpaidGuardians /></ProtectedRoute>} />
            <Route path="/manager/finance" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminFinance /></ProtectedRoute>} />
            <Route path="/manager/leave-requests" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminLeaveRequests /></ProtectedRoute>} />
            <Route path="/manager/exams" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerExams /></ProtectedRoute>} />
            <Route path="/manager/notifications" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><ManagerNotifications /></ProtectedRoute>} />
            <Route path="/manager/calendar" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><SchoolCalendar /></ProtectedRoute>} />
            <Route path="/manager/calls" element={<ProtectedRoute allowedRoles={['manager', 'super_admin']}><AdminCallsList /></ProtectedRoute>} />

            {/* Teacher Routes */}
            <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherDashboard /></ProtectedRoute>} />
            <Route path="/teacher/salary" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherSalary /></ProtectedRoute>} />
            <Route path="/admin/salaries" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminSalaries /></ProtectedRoute>} />
            <Route path="/manager/salaries" element={<ProtectedRoute allowedRoles={['manager']}><ManagerSalaries /></ProtectedRoute>} />
            <Route path="/teacher/students" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherStudents /></ProtectedRoute>} />
            <Route path="/admin/attendance" element={<ProtectedRoute allowedRoles={['super_admin', 'manager', 'branch_manager']}><AdminAttendance /></ProtectedRoute>} />
            <Route path="/teacher/attendance" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherAttendance /></ProtectedRoute>} />
            <Route path="/teacher/homework" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherHomework /></ProtectedRoute>} />
            <Route path="/teacher/exams" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherExams /></ProtectedRoute>} />
            <Route path="/teacher/leave" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherLeave /></ProtectedRoute>} />
            <Route path="/teacher/hours" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherHours /></ProtectedRoute>} />
            <Route path="/teacher/student-of-month" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherStudentOfMonth /></ProtectedRoute>} />
            <Route path="/teacher/notifications" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherNotifications /></ProtectedRoute>} />
            <Route path="/teacher/calendar" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><SchoolCalendar /></ProtectedRoute>} />
            <Route path="/teacher/timetable" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><TeacherTimetable /></ProtectedRoute>} />

            {/* Parent Routes */}
            <Route path="/parent" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentDashboard /></ProtectedRoute>} />
            <Route path="/parent/details" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentDetails /></ProtectedRoute>} />
            <Route path="/parent/children" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentChildren /></ProtectedRoute>} />
            <Route path="/parent/attendance" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentAttendance /></ProtectedRoute>} />
            <Route path="/parent/homework" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentHomework /></ProtectedRoute>} />
            <Route path="/parent/grades" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentGrades /></ProtectedRoute>} />
            <Route path="/parent/payments" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentPayments /></ProtectedRoute>} />
            <Route path="/parent/activities" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentActivities /></ProtectedRoute>} />
            <Route path="/parent/previous-years" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentPreviousYears /></ProtectedRoute>} />
            <Route path="/parent/renewal" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentRenewal /></ProtectedRoute>} />
            <Route path="/parent/policies" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentPolicies /></ProtectedRoute>} />
            <Route path="/teacher/policies" element={<ProtectedRoute allowedRoles={['teacher', 'super_admin']}><ParentPolicies /></ProtectedRoute>} />
            <Route path="/admin/policies" element={<ProtectedRoute allowedRoles={['super_admin']}><ParentPolicies /></ProtectedRoute>} />
            <Route path="/manager/policies" element={<ProtectedRoute allowedRoles={['manager', 'branch_manager', 'super_admin']}><ParentPolicies /></ProtectedRoute>} />
            <Route path="/parent/notifications" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><ParentNotifications /></ProtectedRoute>} />
            <Route path="/parent/calendar" element={<ProtectedRoute allowedRoles={['parent', 'super_admin']}><SchoolCalendar /></ProtectedRoute>} />

            {/* Default Route */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}

function App() {
    useEffect(() => {
        // Swap the favicon to the school logo whenever settings load
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        if (!API_URL) return;
        axios.get(`${API_URL}/api/settings`, { withCredentials: true }).then(res => {
            const logo = res.data?.logo_url;
            if (!logo) return;
            const link = document.getElementById('dynamic-favicon') || document.querySelector("link[rel*='icon']");
            if (link) link.href = logo;
            else {
                const l = document.createElement('link');
                l.rel = 'icon';
                l.id = 'dynamic-favicon';
                l.href = logo;
                document.head.appendChild(l);
            }
        }).catch(() => {});
    }, []);
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <MaintenanceGate>
                        <AppRoutes />
                    </MaintenanceGate>
                    <Toaster position="top-right" richColors />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
